import React from 'react';
import type {SxProps} from '@mui/material';
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import {useGridApiRef} from '@mui/x-data-grid-pro';
import type {RowSelectionModel} from 'components/dataTable/types';
import type {Data, Schema} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {ColorPalette} from 'theme';
import {ThesaurusesGate} from 'services/thesaurus';
import {DataTable} from 'components/dataTable';
import {Layout} from 'components/layout';
import {Spacer} from 'components/spacer';
import type {BasePartProps} from './types';
import {useSchemaThesaurusIds} from '../../hooks';
import {useTableConfigurationContext} from '../contexts/TableConfigurationContextType';
import {sortColumnConfigs} from '../functions';
import {useColumnConfig, useDataTableColumns, useRowFunctions} from '../hooks';
import {mapToVisibilityModel} from '../mapper/mapToVisibilityModel';
import {QuickFilterButtons} from './QuickFilterButtons';

type TableSchemaProps<TData extends Data> = Omit<
  BasePartProps<undefined>,
  'part' | 'valuePath' | 'value' | 'relatedScope'
> & {
  header: React.ReactNode | string;
  schema: Schema;
  rows?: TData[];
  loading?: boolean;
  onRowClick?: (row: TData) => void;
  highlights?: string[];
  selection?: RowSelectionModel;
  onSelectionChanged?: (model: RowSelectionModel) => void;
  onRowsUpdate?: (gridRows: GridValidRowModel[]) => void;
  rowCount?: number;
};

const tableSx: SxProps = {
  p: 2,
  backgroundColor: ColorPalette.primary.white,
  borderRadius: 1,
  height: '100%',
  width: '100%',
  minHeight: '400px',
};

export function TableSchema<TData extends Data>({
  header,
  schema,
  rows,
  loading,
  onRowClick,
  highlights,
  rowCount,
  selection,
  onSelectionChanged,
  onRowsUpdate,
}: TableSchemaProps<TData>) {
  const {tLoose} = useLocalization();
  const {
    indicateRowId,
    disableSorting,
    disableFiltering,
    disableFilteringByName,
    disableColumnResize,
    enableSelectionButton,
    contentHeight,
    partValueRenderers,
    partValueGetters,
    partValueDelimiter,
    autoRowHeight,
    quickFilters,
    enableQuickFilter,
    hidden,
    hideColumnSelector,
    pageSizeAllowAll,
    hideFooter,
    pinnedColumns,
    scrollAfterPagination,
    actions,
  } = useTableConfigurationContext();

  const {
    columnConfigs,
    setColumnConfigs,
    setColumnConfigWidth,
    sortingConfig,
    setSortingConfig,
    setColumnConfigVisibility,
    filterModel,
    setFilterModel,
    paginationModel,
    setPaginationModel,
  } = useColumnConfig(schema);

  const thesaurusIds = useSchemaThesaurusIds(schema);

  const datatableColumns: GridColDef[] = useDataTableColumns(
    schema,
    columnConfigs,
    partValueRenderers,
    partValueGetters,
    partValueDelimiter,
    disableFiltering,
    disableFilteringByName,
    disableSorting,
    highlights,
    indicateRowId,
    actions,
  );

  const {getRowHeight} = useRowFunctions(schema, rows, autoRowHeight);
  const columnVisibilityModel: GridColumnVisibilityModel = mapToVisibilityModel(
    columnConfigs,
    hidden,
  );

  const apiRef = useGridApiRef();

  return (
    <ThesaurusesGate thesauruses={thesaurusIds}>
      {enableQuickFilter ? (
        <>
          <QuickFilterButtons
            setFilterModel={setFilterModel}
            onOpenFilterPanel={() => apiRef.current?.showFilterPanel()}
            quickFilters={quickFilters}
            filterModel={filterModel}
          />
          <Spacer size={3} />
        </>
      ) : null}
      <Layout flex={1} height={contentHeight} position="relative" sx={tableSx}>
        <DataTable
          apiRef={apiRef}
          key={schema.name}
          className={`datatable-${schema.name}`}
          columns={datatableColumns}
          rows={rows ?? []}
          loading={loading !== undefined ? loading : rows === undefined}
          getRowHeight={getRowHeight}
          onRowClick={onRowClick}
          pinnedColumns={pinnedColumns}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
          columnVisibilityModel={columnVisibilityModel}
          setColumnVisibility={(columnName, visible) => {
            setColumnConfigVisibility(columnName, visible);
          }}
          setColumnsOrder={newOrder => {
            setColumnConfigs(sortColumnConfigs(columnConfigs, newOrder));
          }}
          setColumnWidth={
            disableColumnResize
              ? undefined
              : (columnName, width) => {
                  setColumnConfigWidth(columnName, width);
                }
          }
          sorting={disableSorting ? undefined : sortingConfig}
          onSortingChanged={disableSorting ? undefined : setSortingConfig}
          enableSelectionButton={enableSelectionButton}
          selectionModel={selection}
          onSelectionChanged={onSelectionChanged}
          header={header}
          columnSelectionTitle={tLoose(`schema.table.columnselect.title`, {
            name: tLoose(`schema.name.${schema.name}.title`),
          })}
          paginationModel={paginationModel}
          rowCount={rowCount}
          setPaginationModel={setPaginationModel}
          hideFooter={hideFooter}
          onRowsUpdate={onRowsUpdate}
          pageSizeAllowAll={pageSizeAllowAll}
          scrollAfterPagination={scrollAfterPagination}
          slotProps={{
            toolbar: {
              hiddenColumns: hidden ? Object.keys(hidden) : undefined,
              hideColumnSelector,
            },
          }}
        />
      </Layout>
    </ThesaurusesGate>
  );
}
